
import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { NgxPaginationModule } from 'ngx-pagination';
import { finalize } from 'rxjs';

import { UserLoginRole } from 'src/app/enumeration/roles';
import { RedeemApiService } from 'src/app/service/redeem/redeem-api.service';
import { CheckRolesAccessService } from 'src/app/utils/check_roles_access/check-roles-access.service';
import { PageSize } from './../../../enumeration/pageSize';

// Interface structures to satisfy strict template access checking rules
interface RedeemItem {
  srNo?: number | string;
  name: string;
  points: number;
  mmkValue: number;
  serviceName: string;
  minutesValue?: number;
  redeemableType: string;
  enabled: boolean;
}

interface RedeemListResponse {
  total: number;
  allTotal: number;
  redeemableItemRespInfoLst: RedeemItem[];
}

// Strongly Typed Form Structural Mappings
interface NewRedeemForm {
  name: FormControl<string>;
  points: FormControl<string>;
  mmkValue: FormControl<string>;
  serviceName: FormControl<string>;
  minutesValue: FormControl<string | null>;
  redeemableType: FormControl<string>;
}

interface UpdateRedeemForm {
  name: FormControl<string>;
  points: FormControl<string>;
  mmkValue: FormControl<string>;
  serviceName: FormControl<string>;
  minutesValue: FormControl<string | null>;
}

@Component({
  selector: 'app-redeem-items-list',
  standalone: true,
  imports: [CommonModule, NgxPaginationModule, FormsModule, ReactiveFormsModule, RouterModule],
  templateUrl: './redeem-items-list.component.html',
  styleUrl: './redeem-items-list.component.css',
})
export class RedeemItemsListComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly apiService = inject(RedeemApiService);
  private readonly checkAccessRoles = inject(CheckRolesAccessService);
  private readonly destroyRef = inject(DestroyRef);

  // Fully refactored component view data structures using modern signals
  readonly results = signal<RedeemItem[]>([]);
  readonly total = signal<number>(0);
  readonly allTotal = signal<number>(0);
  readonly page = signal<number>(1); // 1-based index template matching parameter
  readonly pageSize = signal<number>(PageSize.REDEEM_ITEMS_LISTS);

  readonly loading = signal<boolean>(false);
  readonly apiLoading = signal<boolean>(false);

  // Structural Domain Options Properties
  readonly itemTypes = ['PHONE_BILL_TOPUP', 'WALLET_CASH_TRANSFER' ];
  isAddNewUpdateAccess = false;
  selectedName = '';

  // Type-Safe Reactive Form Schemas Containers
  newDataForm!: FormGroup<NewRedeemForm>;
  updateDataForm!: FormGroup<UpdateRedeemForm>;

  constructor() {
    this.initializeValidationFormSchemas();
  }

  ngOnInit(): void {
    this.isAddNewUpdateAccess = this.checkAccessRoles.check([UserLoginRole.SUPER_ADMIN]);
    this.listAll();
  }

  private initializeValidationFormSchemas(): void {
    // Upgraded from legacy UntypedFormControls to Angular's type-safe form model architecture
    this.newDataForm = this.fb.group<NewRedeemForm>({
      name: new FormControl('', { validators: [Validators.required], nonNullable: true }),
      points: new FormControl('', { validators: [Validators.required, Validators.pattern('^[0-9]*$')], nonNullable: true }),
      mmkValue: new FormControl('', { validators: [Validators.required, Validators.pattern('^[0-9]*$')], nonNullable: true }),
      serviceName: new FormControl('', { validators: [Validators.required], nonNullable: true }),
      minutesValue: new FormControl(''),
      redeemableType: new FormControl('', { validators: [Validators.required], nonNullable: true }),
    });

    this.updateDataForm = this.fb.group<UpdateRedeemForm>({
      name: new FormControl('', { validators: [Validators.required], nonNullable: true }),
      points: new FormControl('', { validators: [Validators.required, Validators.pattern('^[0-9]*$')], nonNullable: true }),
      mmkValue: new FormControl('', { validators: [Validators.required, Validators.pattern('^[0-9]*$')], nonNullable: true }),
      serviceName: new FormControl('', { validators: [Validators.required], nonNullable: true }),
      minutesValue: new FormControl(''),
    });
  }

  /**
   * Dispatches network lookup commands to stream active reward packages
   */
  listAll(): void {
    this.loading.set(true);
    const zeroBasedPage = this.page() - 1; // Syncs our 1-based page signal to 0-based server requirements

    this.apiService.getRedeemItemsLists(zeroBasedPage)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false)) // Unconditionally turns off active background spinners
      )
      .subscribe({
        next: (data: RedeemListResponse) => {
          this.total.set(data?.total || 0);
          this.allTotal.set(data?.allTotal || 0);
          this.results.set(data?.redeemableItemRespInfoLst || []);
        },
        error: (err: any) => {
          console.error('Failed to load catalog rows entries:', err);
          alert(err?.message || err || 'Network failure while downloading active entries.');
        }
      });
  }

  /**
   * Provisions a brand new exchange package entry straight into production catalogs
   */
  addNewRedeem(): void {
    if (this.newDataForm.invalid) return;

    this.apiLoading.set(true);

    this.apiService.getAddNewRedeemItem(this.newDataForm.value)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.apiLoading.set(false))
      )
      .subscribe({
        next: (data: any) => {
          alert(`Reward Item "${data?.name || 'Package'}" successfully added.`);
          this.newDataForm.reset();
          this.closeModalViaNativeElement('addNewRedeemModal'); // Safe abstract modal dismissal
          this.listAll();
        },
        error: (err: any) => {
          console.error('Catalog registry execution error:', err);
          alert(err?.message || err || 'Failed to persist exchange package item data.');
        }
      });
  }

  /**
   * Pulls detailed metadata for an entry to populate modify fields smoothly
   */
  GetSingleView(name: string): void {
    if (!name) return;

    this.apiLoading.set(true);
    this.updateDataForm.reset();

    this.apiService.getRedeemItemInfo(name)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.apiLoading.set(false))
      )
      .subscribe({
        next: (v: any) => {
          if (!v) return;
          this.selectedName = v.name;

          // Replaced individual verbose control assignments with a single immutable object patch
          this.updateDataForm.patchValue({
            name: v.name,
            points: v.points,
            mmkValue: v.mmkValue,
            serviceName: v.serviceName,
            minutesValue: v.minutesValue || '',
          });
        },
        error: (err: any) => {
          console.error('Reward info lookup error intercept:', err);
          alert(err?.message || err || 'Failed to synchronize item record details view.');
        }
      });
  }

  /**
   * Commits structural parameter overrides back into data stores
   */
  updateRedeem(): void {
    if (this.updateDataForm.invalid) return;

    this.apiLoading.set(true);

    this.apiService.getUpdateRedeemItem(this.updateDataForm.value)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.apiLoading.set(false))
      )
      .subscribe({
        next: () => {
          alert('Reward item updated successfully.');
          this.closeModalViaNativeElement('updateRedeemModal');
          this.listAll();
        },
        error: (err: any) => {
          console.error('Catalog metadata mutation failure exception:', err);
          alert(err?.message || err || 'Failed to commit item updates.');
        }
      });
  }

  private closeModalViaNativeElement(elementId: string): void {
    const targetModalNode = document.getElementById(elementId);
    if (targetModalNode) {
      const bootstrapInstance = (window as any).bootstrap?.Modal?.getInstance(targetModalNode);
      bootstrapInstance?.hide();
    }
  }

  /**
   * Toggles visibility and availability status parameters for a catalog item
   */
  redeemEnableDisable(type: string, name: string): void {
    if (!name) return;

    this.apiLoading.set(true);

    this.apiService.getredeemEnableDisable(type, name)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.apiLoading.set(false)) // Unconditionally drops active processing spinners
      )
      .subscribe({
        next: () => {
          alert('Reward item modified successfully.');
          this.listAll(); // Re-sync active list rows signal matrix automatically
        },
        error: (err: any) => {
          console.error('Visibility toggle transmission failure error:', err);
          alert(err?.message || err || 'Failed to modify catalog item rules configuration.');
        }
      });
  }

  /**
   * Streams a specialized subset of items that are marked active for consumer viewing
   */
  ListAllEnabledRedeem(): void {
    this.loading.set(true);

    this.apiService.getEnabledRedeemableItemList()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false))
      )
      .subscribe({
        next: (data: any) => {
          this.total.set(data?.total || 0);
          this.results.set(data?.redeemableItemRespInfoLst || []);
        },
        error: (err: any) => {
          console.error('Enabled reward assets collection download error:', err);
          alert(err?.message || err || 'Failed to separate active catalog parameters.');
        }
      });
  }

  /**
   * Captures user pagination click inputs to smoothly shift viewing indexes
   */
  handlePageChange(event: number): void {
    if (!this.loading()) {
      this.page.set(event); // Direct 1-based signal mapping directly matching ngx-pagination UI metrics
      this.listAll();
    }
  }

}
